<template>
  <v-container fluid class="pa-0">
    <v-app-bar flat color="red-darken-3">
      <v-app-bar-title class="hidden-sm-and-down">
        {{ $t("price_one_averages.label") }}
      </v-app-bar-title>
      <v-divider class="mx-5 hidden-sm-and-down" inset vertical />
      <v-autocomplete
        v-if="isInstitutionSelectorRole"
        v-model="institution"
        :items="institutions"
        :loading="loadingInstitution"
        :label="$t('parts.advanced_search.institution')"
        item-title="title"
        item-value="_id"
        flat
        variant="solo"
        color="red-darken-1"
        hide-details
        class="hidden-sm-and-down institution-select"
        clearable
      />
      <v-text-field
        v-model="search"
        :label="$t('price_one_averages.search')"
        flat
        variant="solo"
        color="red-darken-1"
        append-inner-icon="mdi-magnify"
        single-line
        hide-details
        class="mx-1"
        @keyup.enter="runSearch"
        @click:append-inner="runSearch"
      />
      <v-switch
        v-model="withAverageOnly"
        :label="$t('price_one_averages.with_average_only')"
        color="white"
        hide-details
        class="hidden-sm-and-down ml-2"
      />
      <v-spacer class="hidden-sm-and-down" />
    </v-app-bar>

    <v-alert :model-value="alert.value" :type="alert.type" class="ma-0">
      {{ alert.message }}
    </v-alert>

    <v-alert
      v-if="isInstitutionSelectorRole && !institution"
      :model-value="true"
      type="info"
      variant="tonal"
      class="ma-4"
    >
      {{ $t("price_one_averages.select_institution_hint") }}
    </v-alert>

    <v-dialog v-model="confirmDialog" width="auto">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            :model-value="true"
            type="warning"
            variant="tonal"
            border="bottom"
          >
            {{ $t("confirm_alert") }}
          </v-alert>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="outlined" @click="confirmDialog = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn
            color="warning"
            variant="outlined"
            :loading="applying"
            @click="applyPrice"
          >
            {{ $t("yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="d-md-none price-one-mobile-list mt-2 px-2 pb-4">
      <v-progress-linear v-if="loading" indeterminate color="red" class="mb-2" />
      <div
        v-for="item in parts"
        :key="rowKey(item)"
        class="price-one-mobile-card"
      >
        <div class="price-one-mobile-card__main">
          <div class="d-flex align-center flex-wrap ga-2">
            <v-btn color="black" variant="outlined" size="x-small" rounded readonly @click.stop>
              {{ item.oemCode }}
            </v-btn>
            <v-icon size="16" class="pointer" @click.stop="copyToClipboard(item.oemCode)">
              mdi-content-copy
            </v-icon>
            <v-chip size="small" variant="tonal">
              {{ $t("price_one_averages.table.count") }}:
              <strong class="ml-1">{{ item.priceOneCount }}</strong>
            </v-chip>
            <v-chip v-if="item.averagePrice != null" size="small" variant="tonal">
              {{ $t("price_one_averages.table.average") }}:
              <strong class="ml-1">{{ item.averagePrice }}</strong>
            </v-chip>
          </div>
          <v-text-field
            :model-value="priceInput(item)"
            :label="$t('price_one_averages.table.new_price')"
            type="number"
            min="2"
            density="compact"
            variant="outlined"
            hide-details
            class="price-input mt-2 w-100"
            @update:model-value="setPriceInput(item, $event)"
          />
          <v-btn
            color="grey-darken-4"
            variant="flat"
            size="small"
            class="mt-2"
            block
            :disabled="enteredPrice(item) == null"
            :loading="applyingKey === rowKey(item)"
            @click="askUpdate(item)"
          >
            {{ $t("price_one_averages.apply") }}
          </v-btn>
          <div class="text-caption text-grey-darken-1 mt-2">
            <span class="font-weight-medium text-grey-darken-2">
              {{ $t("price_one_averages.table.title") }}:
            </span>
            {{ localizedLabel(item.title) || "—" }}
            <v-icon
              v-if="localizedLabel(item.title)"
              size="16"
              class="pointer ml-1"
              @click.stop="copyToClipboard(localizedLabel(item.title))"
            >
              mdi-content-copy
            </v-icon>
          </div>
        </div>
      </div>
      <v-alert v-if="!loading && parts.length < 1 && effectiveInstitutionId" type="info" variant="tonal" class="mt-2">
        No data available.
      </v-alert>
    </div>

    <v-data-table-server
      v-model:page="pagination.page"
      v-model:items-per-page="pagination.itemsPerPage"
      v-model:sort-by="pagination.sortBy"
      must-sort
      :headers="headers"
      :items="parts"
      :items-length="count"
      :loading="loading"
      class="d-none d-md-block elevation-1"
      :items-per-page-options="[
        { value: 5, title: '5' },
        { value: 10, title: '10' },
        { value: 25, title: '25' },
        { value: 50, title: '50' },
      ]"
      items-per-page-text="Rows per page"
      @update:options="updatePagination"
    >
      <template #progress>
        <v-progress-linear color="red" indeterminate />
      </template>
      <template #item="{ item, index }">
        <tr :class="index % 2 === 0 ? 'bg-grey-lighten-4' : ''">
          <td class="text-xs">
            <span class="copyable">{{ localizedLabel(item.title) || "—" }}</span>
            <v-icon
              v-if="localizedLabel(item.title)"
              size="small"
              class="ml-1 pointer"
              @click.stop="copyToClipboard(localizedLabel(item.title))"
            >
              mdi-content-copy
            </v-icon>
          </td>
          <td class="text-xs">
            <v-btn variant="outlined" rounded>
              {{ item.oemCode }}
            </v-btn>
            <v-icon
              size="small"
              class="ml-1 pointer"
              @click.stop="copyToClipboard(item.oemCode)"
            >
              mdi-content-copy
            </v-icon>
          </td>
          <td class="text-xs">
            <v-chip>{{ item.priceOneCount }}</v-chip>
          </td>
          <td class="text-xs">
            <v-chip v-if="item.averagePrice != null">{{ item.averagePrice }}</v-chip>
            <span v-else>—</span>
          </td>
          <td>
            <v-text-field
              :model-value="priceInput(item)"
              type="number"
              min="2"
              density="compact"
              variant="outlined"
              hide-details
              class="price-input"
              @update:model-value="setPriceInput(item, $event)"
            />
          </td>
          <td>
            <v-btn
              color="grey-darken-4"
              variant="flat"
              size="small"
              :disabled="enteredPrice(item) == null"
              :loading="applyingKey === rowKey(item)"
              @click="askUpdate(item)"
            >
              {{ $t("price_one_averages.apply") }}
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table-server>

    <div class="d-md-none px-2 pb-4">
      <v-pagination
        v-if="pageCount > 1"
        v-model="pagination.page"
        :length="pageCount"
        @update:model-value="getParts"
      />
    </div>
  </v-container>
</template>

<script>
import getRequest from "../api/getRequest";
import postRequest from "../api/postRequest";

const INSTITUTION_SELECTOR_ROLES = ["LekoAdmin", "LekoEmployee", "SuperAdmin"];

export default {
  data: () => ({
    headers: [
      { title: "", key: "title", sortable: true },
      { title: "", key: "oemCode", sortable: true },
      { title: "", key: "priceOneCount", sortable: true },
      { title: "", key: "averagePrice", sortable: true },
      { title: "", key: "newPrice", sortable: false },
      { title: "", key: "action", sortable: false },
    ],
    loading: false,
    loadingInstitution: false,
    applying: false,
    parts: [],
    count: 0,
    search: "",
    appliedSearch: "",
    withAverageOnly: false,
    lastQuery: "",
    institution: null,
    institutions: [],
    priceInputs: {},
    pendingItem: null,
    applyingKey: null,
    confirmDialog: false,
    alert: { value: false, type: "success", message: "" },
    pagination: {
      itemsPerPage: 10,
      page: 1,
      sortBy: [{ key: "priceOneCount", order: "desc" }],
    },
  }),
  computed: {
    isInstitutionSelectorRole() {
      return INSTITUTION_SELECTOR_ROLES.includes(this.$store.getters.role);
    },
    effectiveInstitutionId() {
      if (this.isInstitutionSelectorRole) return this.institution;
      return this.$store.getters.institutionId;
    },
    pageCount() {
      const perPage = this.pagination.itemsPerPage || 10;
      return Math.ceil(this.count / perPage);
    },
  },
  watch: {
    "$i18n.locale"() {
      this.getLocale();
    },
    institution() {
      if (this.pagination.page !== 1) this.pagination.page = 1;
      else this.getParts();
    },
    withAverageOnly() {
      if (this.pagination.page !== 1) this.pagination.page = 1;
      else this.getParts();
    },
  },
  created() {
    this.getLocale();
    if (this.isInstitutionSelectorRole) this.getInstitutions();
  },
  methods: {
    getLocale() {
      this.headers[0].title = this.$t("price_one_averages.table.title");
      this.headers[1].title = this.$t("price_one_averages.table.oem");
      this.headers[2].title = this.$t("price_one_averages.table.count");
      this.headers[3].title = this.$t("price_one_averages.table.average");
      this.headers[4].title = this.$t("price_one_averages.table.new_price");
    },
    copyToClipboard(text) {
      if (!text) return;
      navigator.clipboard.writeText(String(text)).then(() => {}).catch(() => {});
    },
    localizedLabel(value) {
      if (!value) return "";
      if (typeof value === "string") return value;
      const lang = this.$store.getters.language;
      return value[lang] || value.it || value.en || value.al || value.sq || "";
    },
    rowKey(item) {
      return `${item.oemCode}::${item.descriptionId || ""}`;
    },
    priceInput(item) {
      const value = this.priceInputs[this.rowKey(item)];
      return value == null ? "" : value;
    },
    setPriceInput(item, value) {
      this.priceInputs = { ...this.priceInputs, [this.rowKey(item)]: value };
    },
    enteredPrice(item) {
      const price = Math.round(Number(this.priceInputs[this.rowKey(item)]));
      if (!Number.isFinite(price) || price <= 1) return null;
      return price;
    },
    seedPrices(parts) {
      const next = { ...this.priceInputs };
      parts.forEach((item) => {
        const key = this.rowKey(item);
        if (!Object.prototype.hasOwnProperty.call(next, key)) {
          next[key] = item.averagePrice != null ? item.averagePrice : "";
        }
      });
      this.priceInputs = next;
    },
    askUpdate(item) {
      if (this.enteredPrice(item) == null) return;
      this.pendingItem = item;
      this.confirmDialog = true;
    },
    showAlert(type, message) {
      this.alert = { value: true, type, message };
      setTimeout(() => {
        this.alert = { value: false, type, message: "" };
      }, 4000);
    },
    getInstitutions() {
      this.loadingInstitution = true;
      getRequest("/institution/")
        .then((response) => {
          this.institutions = response.institutions || [];
        })
        .catch(() => {})
        .finally(() => {
          this.loadingInstitution = false;
        });
    },
    runSearch() {
      this.appliedSearch = (this.search || "").trim();
      if (this.pagination.page !== 1) this.pagination.page = 1;
      else this.getParts();
    },
    updatePagination() {
      this.getParts();
    },
    getParts() {
      if (!this.effectiveInstitutionId) {
        this.parts = [];
        this.count = 0;
        return;
      }
      const sort = this.pagination.sortBy && this.pagination.sortBy[0];
      const sortKey = sort && typeof sort === "object" ? sort.key : sort;
      const sortBy = ["title", "oemCode", "priceOneCount", "averagePrice"].includes(sortKey)
        ? sortKey
        : "priceOneCount";
      const descending = !sort || sort.order !== "asc";
      const page = this.pagination.page || 1;
      const limit = this.pagination.itemsPerPage || 10;
      const signature = [
        this.effectiveInstitutionId,
        page,
        limit,
        sortBy,
        descending,
        this.appliedSearch,
        this.withAverageOnly,
        this.$store.getters.language,
      ].join("|");
      if (signature === this.lastQuery) return;
      this.lastQuery = signature;

      let query = `role=${this.$store.getters.role}&institution_id=${this.effectiveInstitutionId}`;
      query += `&page=${page}&limit=${limit}`;
      query += `&sort_by=${encodeURIComponent(sortBy)}&descending=${descending}`;
      query += `&language=${encodeURIComponent(this.$store.getters.language || "")}`;
      if (this.withAverageOnly) query += "&with_average=true";
      if (this.appliedSearch) query += `&search=${encodeURIComponent(this.appliedSearch)}`;

      this.loading = true;
      getRequest("/part/price-one-averages", query)
        .then((response) => {
          this.parts = response.parts || [];
          this.count = response.count || 0;
          this.seedPrices(this.parts);
        })
        .catch((error) => {
          this.lastQuery = "";
          this.showAlert("error", this.errorMessage(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    applyPrice() {
      const item = this.pendingItem;
      const price = item && this.enteredPrice(item);
      if (!item || price == null || !this.effectiveInstitutionId) return;

      this.applying = true;
      this.applyingKey = this.rowKey(item);
      postRequest
        .object("/part/price-one-averages", {
          institution_id: this.effectiveInstitutionId,
          items: [{
            oemCode: item.oemCode,
            descriptionId: item.descriptionId || null,
            price,
          }],
        })
        .then(() => {
          this.confirmDialog = false;
          this.pendingItem = null;
          const next = { ...this.priceInputs };
          delete next[this.rowKey(item)];
          this.priceInputs = next;
          this.showAlert("success", this.$t("price_one_averages.updated"));
          this.lastQuery = "";
          this.getParts();
        })
        .catch((error) => {
          this.confirmDialog = false;
          this.showAlert("error", this.errorMessage(error));
        })
        .finally(() => {
          this.applying = false;
          this.applyingKey = null;
        });
    },
    errorMessage(error) {
      const data = error && error.response && error.response.data;
      if (typeof data === "string") return data;
      if (data && data.message) return data.message;
      return "Something went wrong";
    },
  },
};
</script>

<style scoped>
.institution-select {
  max-width: 280px;
  min-width: 200px;
}
@media (max-width: 959.98px) {
  .price-input {
    max-width: none;
  }
}
@media (min-width: 960px) {
  .price-input {
    max-width: 120px;
  }
}
.pointer {
  cursor: pointer;
}
.copyable {
  user-select: text;
}
.price-one-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.price-one-mobile-card__main {
  padding: 10px;
}
</style>
